import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button, Spinner } from '@/components/ui';

interface SearchableDropdownProps<T> {
  options: T[];
  value: string | number | undefined | null;
  onChange: (value: string) => void;
  formatOption: (option: T) => string;
  formatDisplay?: (option: T) => string;
  /** Rich option body. Search/filter still uses `formatOption` (and `getSearchText` if set). */
  renderOption?: (option: T) => React.ReactNode;
  /** Extra haystack for local type-ahead. Defaults to `formatOption`. */
  getSearchText?: (option: T) => string;
  getValue: (option: T) => string | number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  readOnly?: boolean;
  readOnlyStyle?: React.CSSProperties;
  label?: string;
  isRequired?: boolean;
  noOptionsMessage?: string;
  // Async search support (optional). If provided, component will fetch options as user types
  fetchOptions?: (query: string) => Promise<T[]>;
  minCharsForSearch?: number;
  // Hide the clear/erase button - useful for mandatory fields
  allowClear?: boolean;
  /** When true, keep the caller's `options` order instead of sorting by label (local options only). */
  preserveOptionsOrder?: boolean;
  /**
   * Minimum width of the portaled menu, in px. The menu is at least as wide as the trigger,
   * and at least this wide — so a narrow table cell does not crush the search field or options.
   */
  menuMinWidth?: number;
  /**
   * Optional action pinned to the bottom of the menu (e.g. "Create new task...").
   * The menu closes before `onClick` runs, so the handler is free to open a modal.
   */
  footerAction?: { label: string; icon?: string; onClick: () => void; disabled?: boolean };
}

export function SearchableDropdown<T>({
  options,
  value,
  onChange,
  formatOption,
  formatDisplay,
  renderOption,
  getSearchText,
  getValue,
  placeholder = 'Select an option',
  disabled = false,
  required = false,
  className = '',
  style = {},
  readOnly = false,
  readOnlyStyle = {},
  label,
  isRequired = false,
  noOptionsMessage = 'No options available',
  fetchOptions,
  minCharsForSearch = 2,
  allowClear = true,
  preserveOptionsOrder = false,
  menuMinWidth = 0,
  footerAction,
}: SearchableDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuPortalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedOptionRef = useRef<HTMLButtonElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number; isUpward: boolean } | null>(null);
  const [remoteOptions, setRemoteOptions] = useState<T[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>(350);
  const [isUpward, setIsUpward] = useState(false);

  // Debounced search for async mode
  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!fetchOptions) {
        setRemoteOptions(null);
        return;
      }
      const q = searchQuery.trim();
      if (q.length < minCharsForSearch) {
        setRemoteOptions([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const results = await fetchOptions(q);
        if (active) setRemoteOptions(results || []);
      } catch (e) {
        if (active) setRemoteOptions([]);
      } finally {
        if (active) setIsSearching(false);
      }
    };
    const t = setTimeout(run, 300);
    return () => { active = false; clearTimeout(t); };
  }, [searchQuery, fetchOptions, minCharsForSearch]);

  // Compute options to show
  const sourceOptions = fetchOptions ? (remoteOptions || []) : options;
  const filteredOptions = fetchOptions
    ? sourceOptions
    : (() => {
        const q = searchQuery.toLowerCase();
        const filtered = sourceOptions.filter((option) => {
          const primary = formatOption(option).toLowerCase();
          const extra = getSearchText ? getSearchText(option).toLowerCase() : '';
          return primary.includes(q) || extra.includes(q);
        });
        if (preserveOptionsOrder) return filtered;
        return filtered.sort((a, b) => {
          const aText = formatOption(a);
          const bText = formatOption(b);
          const aNum = parseFloat(aText);
          const bNum = parseFloat(bText);
          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
          return aText.localeCompare(bText);
        });
      })();

  // Find the selected option
  const selectedOption = [...options, ...(remoteOptions || [])].find(option => {
    const optionValue = getValue(option);
    const compareValue = value?.toString();
    return optionValue?.toString() === compareValue;
  });

  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const modalContent = dropdownRef.current?.closest('.modal-content');
    let isUpward = false;
    if (modalContent) {
      const modalRect = modalContent.getBoundingClientRect();
      const spaceBelow = modalRect.bottom - rect.bottom;
      const spaceAbove = rect.top - modalRect.top;
      isUpward = spaceBelow < 250 && spaceAbove > spaceBelow;
    }
    const viewportPad = 8;
    const maxWidth = Math.max(0, window.innerWidth - viewportPad * 2);
    const width = Math.min(Math.max(rect.width, menuMinWidth), maxWidth || Math.max(rect.width, menuMinWidth));
    let left = rect.left;
    if (left + width > window.innerWidth - viewportPad) {
      left = Math.max(viewportPad, window.innerWidth - viewportPad - width);
    }
    if (left < viewportPad) left = viewportPad;
    setMenuPosition({
      top: isUpward ? rect.top : rect.bottom,
      left,
      width,
      isUpward,
    });
  };

  useLayoutEffect(() => {
    if (isOpen) {
      updateMenuPosition();
    } else {
      setMenuPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen]);

  // Handle click outside to close dropdown (trigger and portaled menu)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = dropdownRef.current?.contains(target);
      const inMenu = menuPortalRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Adjust dropdown position and size when it opens, especially in modals
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      // Small delay to ensure dropdown menu is rendered
      const adjustDropdown = () => {
        // Find the modal container
        const modalContent = dropdownRef.current?.closest('.modal-content');
        
        if (modalContent) {
          // Calculate available space
          const rect = dropdownRef.current!.getBoundingClientRect();
          const modalRect = modalContent.getBoundingClientRect();
          
          // Calculate space below and above the input
          const spaceBelow = modalRect.bottom - rect.bottom;
          const spaceAbove = rect.top - modalRect.top;
          
          // Determine if we should flip upward
          const shouldFlipUpward = spaceBelow < 250 && spaceAbove > spaceBelow;
          setIsUpward(shouldFlipUpward);
          
          // Calculate max height based on available space
          const availableSpace = shouldFlipUpward ? spaceAbove : spaceBelow;
          const calculatedMaxHeight = Math.max(150, Math.min(availableSpace - 10, 250));
          setDropdownMaxHeight(calculatedMaxHeight);
        } else {
          // Not in a modal, use default
          setDropdownMaxHeight(350);
          setIsUpward(false);
        }
      };
      
      // Focus search input immediately
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      
      // Adjust dropdown position/size after a brief delay to ensure it's rendered
      setTimeout(adjustDropdown, 10);
      
      // Scroll selected option into view within the dropdown only (avoid scrolling the page)
      setTimeout(() => {
        const container = optionsContainerRef.current;
        const option = selectedOptionRef.current;
        if (container && option) {
          const optionTop = option.offsetTop;
          const optionBottom = optionTop + option.offsetHeight;
          const containerScrollTop = container.scrollTop;
          const containerHeight = container.clientHeight;
          if (optionTop < containerScrollTop) {
            container.scrollTop = optionTop;
          } else if (optionBottom > containerScrollTop + containerHeight) {
            container.scrollTop = optionBottom - containerHeight;
          }
        }
      }, 50);
    } else {
      // Reset when closed
      setIsUpward(false);
      setDropdownMaxHeight(350);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled && !readOnly) {
      if (!isOpen) {
        // Set position in the same tick so the portal can render on first paint (avoids flicker)
        updateMenuPosition();
      }
      setIsOpen(!isOpen);
      setSearchQuery('');
    }
  };

  const handleOptionSelect = (option: T) => {
    const newValue = getValue(option).toString();
    console.log('Option selected:', {
      option,
      newValue,
      formattedValue: formatOption(option)
    });
    onChange(newValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClearFilter = () => {
    onChange('');
    setSearchQuery('');
  };

  const renderLabel = () => {
    if (!label) return null;
    return (
      <label className="tw-mb-2">
        {label} {isRequired && <span className="tw-text-danger">*</span>}
      </label>
    );
  };

  return (
    <div 
      className={`searchable-dropdown ${isUpward ? 'dropdown-upward' : ''}`} 
      ref={dropdownRef}
    >
      {renderLabel()}
      <div className="tw-relative">
        {/* Main input with integrated arrow */}
        <div className="tw-relative" ref={triggerRef}>
          <input
            type="text"
            className={`tw-block tw-w-full tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-1.5 focus:tw-shadow-focus ${className}`}
            value={selectedOption ? (formatDisplay ? formatDisplay(selectedOption) : formatOption(selectedOption)) : ''}
            placeholder={placeholder}
            onClick={handleToggle}
            readOnly
            disabled={disabled}
            style={{
              cursor: disabled || readOnly ? 'not-allowed' : 'pointer',
              paddingRight: '30px',
              ...(readOnly ? readOnlyStyle : {}),
              ...style
            }}
          />
          {!readOnly && (
            <i
              className={`bi bi-chevron-${isOpen ? 'up' : 'down'} tw-absolute`}
              style={{ 
                right: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6c757d'
              }}
            />
          )}
        </div>

        {/* Dropdown menu: portaled to document.body so it is not clipped by ancestor overflow */}
        {isOpen && !readOnly && menuPosition && createPortal(
          <div
            ref={menuPortalRef}
            className="tw-bg-surface tw-border tw-border-line tw-rounded tw-shadow-sm"
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              minWidth: menuPosition.width,
              ...(menuPosition.isUpward && { transform: 'translateY(-100%)' }),
              maxHeight: `${dropdownMaxHeight}px`,
              overflow: 'hidden',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1080,
            }}
          >
            {/* Search input */}
            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1 tw-border-b tw-border-line tw-bg-surface tw-px-2 tw-py-2">
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-stretch">
                <span className="tw-flex tw-shrink-0 tw-items-center tw-border tw-border-line-input tw-bg-surface-alt tw-px-2">
                  <i className="bi bi-search" aria-hidden="true" />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="tw-min-w-0 tw-flex-1 tw-border tw-border-l-0 tw-border-line-input tw-bg-surface tw-px-2 tw-py-1.5 tw-text-sm focus:tw-border-line-focus focus:tw-outline-none focus:tw-shadow-focus"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchQuery && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    className="tw-shrink-0 tw-rounded-l-none"
                    aria-label="Clear search"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchQuery('');
                    }}
                  >
                    <i className="bi bi-x" />
                  </Button>
                )}
              </div>
              {allowClear && (
                <Button
                  variant="link"
                  size="sm"
                  className="tw-ml-1 tw-shrink-0 tw-text-content-muted"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFilter();
                  }}
                  title="Remove Filter"
                  aria-label="Remove filter"
                >
                  <i className="bi bi-eraser" />
                </Button>
              )}
            </div>

            {/* Options list */}
            <div ref={optionsContainerRef} className="options-container tw-py-1" style={{ overflowY: 'auto', overflowX: 'hidden', flex: '1 1 auto', minHeight: 0 }}>
              {fetchOptions && searchQuery.trim().length < minCharsForSearch && (
                <div className="tw-px-4 tw-py-1 tw-text-content-muted">
                  Please type the task you are looking for. Results will appear after you pause typing.
                </div>
              )}
              {isSearching && (
                <div className="tw-flex tw-items-center tw-px-4 tw-py-1 tw-text-content-muted">
                  <Spinner animation="border" size="sm" className="tw-mr-2" aria-hidden="true" />
                  Searching...
                </div>
              )}
              {filteredOptions.length === 0 ? (
                <div className="tw-px-4 tw-py-1 tw-text-content-muted">
                  {noOptionsMessage}
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const optionValue = getValue(option).toString();
                  const isSelected = optionValue === value?.toString();
                  return (
                    <button
                      key={index}
                      ref={isSelected ? selectedOptionRef : null}
                      type="button"
                      aria-label={formatOption(option)}
                      className={`tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-text-left tw-text-sm ${isSelected ? 'tw-bg-primary tw-text-white' : ''}`}
                      onClick={() => handleOptionSelect(option)}
                      style={{
                        padding: '0.5rem 1rem',
                        borderLeft: isSelected ? '4px solid var(--bs-primary)' : '4px solid transparent'
                      }}
                    >
                      <span className="tw-min-w-0 tw-flex-1">{renderOption ? renderOption(option) : formatOption(option)}</span>
                      {isSelected && <i className="bi bi-check2" />}
                    </button>
                  );
                })
              )}
            </div>

            {/* Pinned footer action */}
            {footerAction && (
              <div className="tw-shrink-0 tw-border-t tw-border-line tw-bg-surface tw-p-1">
                <button
                  type="button"
                  disabled={footerAction.disabled}
                  className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-font-medium tw-text-primary hover:tw-bg-surface-hover disabled:tw-opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                    setSearchQuery('');
                    footerAction.onClick();
                  }}
                >
                  {footerAction.icon && <i className={`bi ${footerAction.icon}`} aria-hidden="true" />}
                  {footerAction.label}
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
} 